import { NonEmptyArray } from 'fp-ts/NonEmptyArray';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
    Tabs as OptimusTabs,
    TabsProps as OptimusTabsProps,
    Stack,
    StackProps,
    Tab,
    TabProps,
} from '@optimus-web/core';
import { StickyContainer } from './StickyContainer';

interface WithTabs<T extends string> {
    disabledTabs?: NonEmptyArray<T>;
    tabs: NonEmptyArray<T>;
    initialTab?: T;
}

interface TabsProps<T extends string> extends Omit<OptimusTabsProps, 'activeTabValue' | 'children'> {
    tabLabels: Record<T, ReactNode>;
    tabProps?: Record<T, Omit<TabProps, 'id' | 'value' | 'label'>>;
    sticky?: boolean;
}

export interface TabbedContentProps<T extends string> extends TabsProps<T> {
    disabledTabs?: NonEmptyArray<T>;
    initialTab?: T;
    tabs: NonEmptyArray<T>;
    children: ({ matchTabs }: { matchTabs: (matchingMap: Record<T, () => ReactNode>) => ReactNode }) => ReactNode;
    spacing?: StackProps['spacing'];
}

export const useTabbedContent = <T extends string>({ disabledTabs, tabs, initialTab }: WithTabs<T>) => {
    const [currentTab, setCurrentTab] = useState<T>(initialTab ?? tabs[0]);

    useEffect(() => {
        setCurrentTab(initialTab ?? tabs[0]);
    }, [initialTab, tabs]);

    return {
        Tabs: useMemo(
            () =>
                ({ onTabClick, tabLabels, tabProps, sticky, ...other }: TabsProps<T>) => {
                    const tabsComponent = (
                        <OptimusTabs
                            {...other}
                            activeTabValue={currentTab}
                            onTabClick={(event, value) => {
                                if (onTabClick) {
                                    onTabClick(event, value);
                                }
                                setCurrentTab(value);
                            }}
                        >
                            {tabs.map((tab) => (
                                <Tab
                                    key={tab}
                                    id={tab}
                                    disabled={disabledTabs?.includes(tab)}
                                    value={tab}
                                    label={tabLabels[tab]}
                                    {...(tabProps ? tabProps[tab] : {})}
                                />
                            ))}
                        </OptimusTabs>
                    );
                    return sticky ? <StickyContainer>{tabsComponent}</StickyContainer> : tabsComponent;
                },
            [currentTab, tabs, disabledTabs]
        ),
        matchTabs: (matchingMap: Record<T, () => ReactNode>) => matchingMap[currentTab](),
        state: {
            currentTab,
            setCurrentTab,
        },
    };
};

export const TabbedContent = <T extends string>({
    disabledTabs,
    initialTab,
    tabs,
    children,
    spacing,
    ...other
}: TabbedContentProps<T>) => {
    const { Tabs, state, matchTabs } = useTabbedContent({ tabs, initialTab, disabledTabs });
    return (
        <Stack vertical spacing={spacing ?? '400'}>
            <Tabs {...other} />
            {children({ matchTabs, ...state })}
        </Stack>
    );
};
